import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import UnitCard from "@/components/UnitCard";
import CopyDescriptionDialog from "@/components/CopyDescriptionDialog";
import ManageUnitsDialog from "@/components/ManageUnitsDialog";
import MarkAllDoneDialog from "@/components/MarkAllDoneDialog";
import MarkUnitDoneDialog from "@/components/MarkUnitDoneDialog";
import PhotoCaptureSheet from "@/components/PhotoCaptureSheet";
import CustomFieldsDialog from "@/components/CustomFieldsDialog";
import AssigneePicker from "@/components/AssigneePicker";
import { derivedStatus } from "@/lib/unitStatus";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Share2, Eye, ChevronsDown, ChevronsUp, ChevronRight,
  Settings, Download, Check, Loader2, CheckCheck, ListChecks,
  Lock, AlertTriangle, X as XIcon, Building2, MoreVertical, Pencil, Smartphone,
} from "lucide-react";
import MoveToFolderDialog from "@/components/MoveToFolderDialog";

export default function ReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copyDialog, setCopyDialog] = useState(null);
  const [manageDialog, setManageDialog] = useState(false);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [contentTarget, setContentTarget] = useState({ tab: null, kind: null });
  const [markAllDoneOpen, setMarkAllDoneOpen] = useState(false);
  const [markUnitDoneTarget, setMarkUnitDoneTarget] = useState(null); // {id, label, ...} or null
  const [customFieldsOpen, setCustomFieldsOpen] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandKey, setExpandKey] = useState(0);
  const [saveState, setSaveState] = useState("saved"); // saving | saved
  const pendingRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reports/${reportId}`);
      setReport(data);
    } catch {
      toast.error("Raporttia ei löytynyt");
      navigate("/app");
    }
  }, [reportId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Dynamic browser/PDF document title — also drives default PDF filename
  // when user prints from this editor view (Cmd+P / Tulosta).
  useEffect(() => {
    if (!report) return;
    const label = (report.title || report.work_type || "").trim();
    const prev = document.title;
    document.title = label ? `Raportti - ${label}` : "Raportti";
    return () => { document.title = prev; };
  }, [report]);

  const updateUnit = async (unitId, patch) => {
    pendingRef.current += 1;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/reports/${reportId}/units/${unitId}`, patch);
      setReport(data);
    } catch {
      toast.error("Tallennus epäonnistui");
    } finally {
      pendingRef.current -= 1;
      if (pendingRef.current <= 0) {
        pendingRef.current = 0;
        setSaveState("saved");
      }
    }
  };

  const updateReport = async (patch) => {
    pendingRef.current += 1;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/reports/${reportId}`, patch);
      setReport(data);
    } catch {
      toast.error("Tallennus epäonnistui");
    } finally {
      pendingRef.current -= 1;
      if (pendingRef.current <= 0) {
        pendingRef.current = 0;
        setSaveState("saved");
      }
    }
  };

  const saveAssignees = async (ids) => {
    pendingRef.current += 1;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/reports/${reportId}/assignees`, { assignees: ids });
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Työntekijöiden tallennus epäonnistui");
    } finally {
      pendingRef.current -= 1;
      if (pendingRef.current <= 0) {
        pendingRef.current = 0;
        setSaveState("saved");
      }
    }
  };

  const [confirmRegen, setConfirmRegen] = useState(false);

  const runGenerateSummary = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/summary`);
      setReport({ ...report, ai_summary: data.ai_summary });
      toast.success("Yhteenveto päivitetty");
    } catch {
      toast.error("Yhteenvedon luonti epäonnistui");
    } finally {
      setGenerating(false);
    }
  };

  const generateSummary = async () => {
    // If summary already exists, confirm before overwriting user edits.
    if ((report?.ai_summary || "").trim()) {
      setConfirmRegen(true);
      return;
    }
    await runGenerateSummary();
  };

  const markSent = async () => {
    await updateReport({ status: "sent" });
    toast.success("Merkitty lähetetyksi");
  };

  const [completionModal, setCompletionModal] = useState(null); // {follow_ups: []} when shown
  const [moveOpen, setMoveOpen] = useState(false);

  // Camera capture (Mobile UX v2 Phase 4)
  // - null: closed
  // - { unit }: photo sheet open for that unit
  // - "pick": unit picker open
  const [cameraTarget, setCameraTarget] = useState(null);

  const openCameraForUnit = (unit) => {
    if (!unit) return;
    // Double-tap / swipe-left on a unit card: open device camera directly.
    setCameraTarget({ unit, autoCamera: true });
  };

  const markComplete = async () => {
    // 1) auto-summary if not present yet
    let didGenerate = false;
    if (!report.ai_summary && !generating) {
      try {
        setGenerating(true);
        const { data } = await api.post(`/reports/${reportId}/summary`);
        setReport((r) => ({ ...r, ai_summary: data.ai_summary }));
        didGenerate = true;
      } catch (_e) {
        // continue even if summary fails
      } finally {
        setGenerating(false);
      }
    }
    // 2) lock report
    await updateReport({ status: "completed" });
    toast.success(didGenerate ? "Raportti valmistui — yhteenveto luotu" : "Raportti valmistui");

    // 3) if there are follow-ups, show estimate modal
    const followUps = report.units.filter((u) => (u.additional_work_text || "").trim());
    if (followUps.length > 0) {
      setCompletionModal({ followUps });
    }
  };

  void markSent;

  const [shareOpen, setShareOpen] = useState(false);
  const [editTokenCache, setEditTokenCache] = useState(null);

  const openShareDialog = async () => {
    if (!report?.report_id) return;
    setShareOpen(true);
    // Lazy-load edit_token (mint on backend if missing) so the dialog opens fast.
    if (!editTokenCache) {
      try {
        const { data } = await api.get(`/reports/edit-token/${report.report_id}`);
        setEditTokenCache(data.edit_token);
      } catch {
        toast.error("Muokkauslinkin haku epäonnistui");
      }
    }
  };

  const copyLink = async (url, kind) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(kind === "edit" ? "Muokkauslinkki kopioitu" : "Lukulinkki kopioitu");
    } catch {
      toast.message("Kopioi linkki manuaalisesti", { description: url });
    }
  };

  const copyShare = openShareDialog;

  const openPreview = () => {
    if (!report?.share_token) return;
    window.open(`/r/${report.share_token}`, "_blank", "noopener");
  };

  const downloadPdf = () => {
    if (!report?.share_token) return;
    // Open the public report in a new window which will auto-trigger print
    const w = window.open(`/r/${report.share_token}?print=1`, "_blank");
    if (!w) toast.error("Salli ponnahdusikkunat ladataksesi PDF");
  };

  const toggleAll = () => {
    setAllExpanded((v) => !v);
    setExpandKey((k) => k + 1);
  };

  const handleCopyDescription = async (description, target_unit_ids, only_empty) => {
    try {
      const { data } = await api.post(`/reports/${reportId}/copy-description`, {
        description, target_unit_ids, only_empty,
      });
      setReport(data.report);
      toast.success(`Toimenpiteet kopioitu ${data.updated} kohteeseen`);
      setCopyDialog(null);
    } catch {
      toast.error("Kopiointi epäonnistui");
    }
  };

  if (!report) {
    return <div className="p-8 text-slate-500">Ladataan...</div>;
  }

  const followUps = report.units.filter((u) => (u.additional_work_text || "").trim());

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-5 md:py-7">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate("/app")} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="detail-back">
            <ArrowLeft className="h-4 w-4" /> Takaisin
          </button>
          <SaveIndicator state={saveState} />
        </div>

        {/* Breadcrumb — työtyyppi / asiakas */}
        <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-2 truncate" data-testid="detail-breadcrumb">
          {[report.work_type, report.customer_name].filter(Boolean).join(" / ") || "Raportti"}
        </div>

        {/* Title + short meta */}
        <div className="mb-5">
          <InlineTitle
            value={report.title}
            onSave={(v) => updateReport({ title: v })}
            testId="report-title-input"
          />
          <p className="text-slate-500 mt-1 text-sm">
            {report.property_type && <>{report.property_type} · </>}{report.unit_count} yksikkö{report.unit_count !== 1 ? "ä" : ""}
          </p>
        </div>

        {/* Action toolbar — Esikatsele PDF · Lataa PDF · Avaa mobiiliraportointi · Jaa linkki · Lisää toimintoja */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 mb-6" data-testid="detail-toolbar">
          <ActionBtn onClick={openPreview} icon={Eye} label="Esikatsele PDF" testId="preview-open-button" />
          <ActionBtn onClick={downloadPdf} icon={Download} label="Lataa PDF" testId="download-pdf-button" />
          <ActionBtn onClick={() => navigate(`/app/raportti/${report.report_id}/kentta`)} icon={Smartphone} label="Avaa mobiiliraportointi" testId="open-mobile-reporting-button" accent="emerald" />
          <ActionBtn onClick={copyShare} icon={Share2} label="Jaa linkki" testId="share-button" />
          <MoreActionsMenu
            onMove={() => setMoveOpen(true)}
            onReopen={
              (report.status === "completed" || report.status === "sent")
                ? () => updateReport({ status: "in_progress" })
                : null
            }
          />
        </div>

        {/* Raportoitavat kohteet — Hallinnoi yksiköitä (full-width, ensimmäisenä) */}
        <SectionGroup
          title="Raportoitavat kohteet"
          description="Lisää, poista tai muokkaa raportoitavia yksiköitä."
          testId="section-units-mgmt"
        >
          <GroupCard
            icon={Building2}
            title="Hallinnoi yksiköitä"
            description={`${report.units.length} ${report.units.length === 1 ? "yksikkö" : "yksikköä"} lisätty`}
            onClick={() => setManageDialog(true)}
            testId="manage-units-button"
          />
        </SectionGroup>

        {/* Raportin valmistelu — 2x2 -korttiruudukko + oletusasetukset */}
        <PreparationSection
          report={report}
          onOpenChecklist={(kind) => { setContentTarget({ tab: "checklist", kind }); setContentDialogOpen(true); }}
          onOpenMaterials={() => { setContentTarget({ tab: "materials", kind: null }); setContentDialogOpen(true); }}
          onOpenCustomFields={() => setCustomFieldsOpen(true)}
          onToggle={(patch) => updateReport(patch)}
        />

        {/* (Poistettu erillinen "Raportoitavat kohteet" -osio — se on nyt tämän blokin yläpuolella) */}

        {/* Perustiedot — kompakti tietorivi */}
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white px-4 py-4" data-testid="detail-project-info">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(report.created_by_name || report.created_by_role) && (
              <InlineField
                label="Raportin laatija"
                value={report.created_by_name}
                onSave={(v) => updateReport({ created_by_name: v })}
                placeholder="esim. Matti Meikäläinen"
                testId="detail-created-by"
              />
            )}
            <InlineField label="Asiakas" value={report.customer_name} onSave={(v) => updateReport({ customer_name: v })} testId="detail-customer" />
            <InlineField label="Osoite" value={report.site_address} onSave={(v) => updateReport({ site_address: v })} testId="detail-address" />
            <InlineDateField
              label="Päivämäärä"
              value={report.report_date || report.created_at}
              onSave={(v) => updateReport({ report_date: v })}
              testId="detail-date"
            />
            {report.ask_executor !== false ? (
              <div className="group/exec relative">
                <InlineField label="Suorittaja" value={report.executor_company} onSave={(v) => updateReport({ executor_company: v })} testId="detail-executor" />
                <button
                  onClick={() => updateReport({ ask_executor: false })}
                  className="absolute -top-1 -right-1 opacity-0 group-hover/exec:opacity-100 transition-opacity p-1 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-red-600"
                  title="Piilota suorittaja-kenttä"
                  data-testid="detail-hide-executor"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => updateReport({ ask_executor: true })}
                className="text-left px-3 py-2 rounded-lg border border-dashed border-slate-200 text-xs text-slate-400 hover:border-slate-300 hover:text-slate-600 transition-colors"
                data-testid="detail-show-executor"
              >
                + Lisää suorittaja-kenttä
              </button>
            )}
            <InlineField label="Kohdetyyppi" value={report.property_type} onSave={(v) => updateReport({ property_type: v })} placeholder="esim. Kerrostalo" testId="detail-property-type" />
          </div>
        </div>

        {/* Kohdenna käyttäjälle — määrää yksi tai useampi kenttätyöntekijä. He
            näkevät työn /kentta-näkymässään. Owner-only. */}
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white px-4 py-4" data-testid="detail-assignees-panel">
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Kohdenna käyttäjälle</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Valitut työntekijät näkevät tämän työn /kentta-näkymässä.
              </div>
            </div>
          </div>
          <AssigneePicker
            value={report.assignees || []}
            onChange={saveAssignees}
            testIdPrefix="detail-assignees"
          />
        </div>

        {/* Raportin yhteenveto */}
        <TopSummaryCard
          report={report}
          generating={generating}
          onGenerate={generateSummary}
          onSave={(v) => updateReport({ ai_summary: v })}
        />

        {/* Completion banner with follow-ups */}
        {report.status === "completed" && followUps.length > 0 && !completionModal && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 md:p-5 mb-6 flex items-start gap-3" data-testid="completion-banner">
            <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-slate-900">Havaittiin {followUps.length} jatkotyö{followUps.length === 1 ? "" : "tä"}</div>
              <div className="text-sm text-slate-600 mt-0.5">Voit antaa työnjohdolle alustavan arvion työmäärästä, tarvikkeista ja kiireellisyydestä.</div>
            </div>
            <button onClick={() => setCompletionModal({ followUps })} className="btn-primary text-sm" data-testid="open-estimates-button">
              Arvioi jatkotyöt
            </button>
          </div>
        )}

        {/* Havainnot yhteenveto — toimiston näkymässä yksiköittäin (ilman esikatselun avaamista) */}
        {(() => {
          if (report.hide_findings_section) return null;
          const items = (report.units || []).map((u) => {
            const findingChecks = (u.checklist || [])
              .filter((c) => c.checked && c.is_finding)
              .map((c) => c.label);
            const extraLines = ((u.additional_work_text || "").trim() || "")
              .split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
            const bullets = [...findingChecks, ...extraLines];
            return { id: u.id, label: u.label, bullets };
          }).filter((x) => x.bullets.length > 0);
          if (items.length === 0) return null;
          return (
            <div className="rounded-2xl border border-slate-200 border-l-[3px] border-l-amber-500 bg-white p-4 md:p-5 mb-5" data-testid="detail-findings-summary">
              <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
                <AlertTriangle className="h-3 w-3 text-amber-600" /> Havainnot ({items.length})
              </div>
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={it.id} className="text-[14px]" data-testid={`detail-finding-${it.id}`}>
                    <div className="font-semibold text-slate-900">{it.label}</div>
                    <ul className="mt-0.5 text-slate-800 space-y-0.5">
                      {it.bullets.map((label, i) => (
                        <li key={i} className="flex items-baseline gap-2">
                          <span className="text-slate-400 leading-none">•</span>
                          <span className="flex-1">{label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* 4) Units list */}
        <div className="space-y-3" data-testid="units-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="min-w-0">
              <div className="font-display font-semibold text-slate-900 text-base inline-flex items-center gap-2">
                Yksiköt
                <span className="text-xs font-medium text-slate-400">({report.units.length} kpl)</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Klikkaa yksikköä avataksesi sen tiedot. Muutokset tallentuvat automaattisesti.</p>
            </div>
          </div>
          {report.units.map((u, idx) => (
            <UnitCard
              key={`${u.id}-${expandKey}`}
              report={report}
              unit={u}
              onUpdate={(patch) => updateUnit(u.id, patch)}
              onCopyDescription={(unit) => setCopyDialog({ sourceUnit: unit })}
              onReportRefresh={(updated) => setReport(updated)}
              onRequestMarkDone={(unit) => setMarkUnitDoneTarget(unit)}
              onOpenCamera={openCameraForUnit}
              defaultExpanded={allExpanded || idx === 0}
            />
          ))}
        </div>
      </div>

      {copyDialog && (
        <CopyDescriptionDialog
          sourceUnit={copyDialog.sourceUnit}
          allUnits={report.units}
          onClose={() => setCopyDialog(null)}
          onCopy={handleCopyDescription}
        />
      )}

      {manageDialog && (
        <ManageUnitsDialog
          report={report}
          mode="units-only"
          onClose={() => setManageDialog(false)}
          onChanged={(updated) => setReport(updated)}
        />
      )}

      {contentDialogOpen && (
        <ManageUnitsDialog
          report={report}
          mode="content"
          initialTab={contentTarget.tab}
          initialKind={contentTarget.kind}
          onClose={() => { setContentDialogOpen(false); setContentTarget({ tab: null, kind: null }); }}
          onChanged={(updated) => setReport(updated)}
        />
      )}

      {customFieldsOpen && (
        <CustomFieldsDialog
          report={report}
          onClose={() => setCustomFieldsOpen(false)}
          onSaved={async (custom_fields) => {
            await updateReport({ custom_fields });
          }}
        />
      )}

      {markAllDoneOpen && report && (
        <MarkAllDoneDialog
          report={report}
          onClose={() => setMarkAllDoneOpen(false)}
          onCompleted={async ({ summaryGenerated }) => {
            setMarkAllDoneOpen(false);
            // Refresh report so units + ai_summary are up to date
            try {
              const { data } = await api.get(`/reports/${report.report_id}`);
              setReport(data);
            } catch { /* no-op */ }
            if (summaryGenerated) {
              // Scroll to top so the summary card is in view
              setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
            }
          }}
        />
      )}

      {markUnitDoneTarget && report && (() => {
        const units = report.units || [];
        const currentIdx = units.findIndex((u) => u.id === markUnitDoneTarget.id);
        const nextUnit = units
          .slice(currentIdx + 1)
          .concat(units.slice(0, currentIdx))
          .find((u) => derivedStatus(u) !== "done" && derivedStatus(u) !== "inaccessible") || null;
        return (
          <MarkUnitDoneDialog
            unit={markUnitDoneTarget}
            nextUnit={nextUnit ? { id: nextUnit.id, label: nextUnit.label } : null}
            onClose={() => setMarkUnitDoneTarget(null)}
            onConfirm={async () => {
              await updateUnit(markUnitDoneTarget.id, { status: "done" });
            }}
            onContinueToNext={(target) => {
              // Force-expand the next unit by remounting via expandKey, then scroll.
              setAllExpanded(false);
              setExpandKey((k) => k + 1);
              setTimeout(() => {
                const el = document.querySelector(`[data-testid="unit-card-${target.id}"]`);
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  // Auto-expand by clicking the toggle if currently collapsed
                  const toggle = el.querySelector(`[data-testid="unit-toggle-${target.id}"]`);
                  if (toggle && !el.querySelector(".border-t")) toggle.click();
                }
              }, 250);
            }}
          />
        );
      })()}

      {shareOpen && (
        <ShareLinksDialog
          shareToken={report.share_token}
          editToken={editTokenCache}
          onClose={() => setShareOpen(false)}
          onCopy={copyLink}
        />
      )}

      {completionModal && (
        <FollowUpEstimateModal
          report={report}
          followUps={completionModal.followUps}
          onClose={() => setCompletionModal(null)}
          onSave={async (estimates) => {
            try {
              for (const [unit_id, est] of Object.entries(estimates)) {
                await api.patch(`/reports/${reportId}/units/${unit_id}`, {
                  follow_up_estimate: est,
                });
              }
              await load();
              toast.success("Arviot tallennettu");
              setCompletionModal(null);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
            }
          }}
        />
      )}

      {moveOpen && (
        <MoveToFolderDialog
          kind="report"
          itemId={reportId}
          currentFolderId={report.folder_id}
          onClose={() => setMoveOpen(false)}
          onMoved={(newFolderId) => setReport((r) => ({ ...r, folder_id: newFolderId }))}
        />
      )}

      {/* Camera FAB on office view removed — office workers don't capture photos here.
          Photo capture is done by field workers via /r-edit/{token}. */}

      {cameraTarget && (
        <PhotoCaptureSheet
          report={report}
          unit={cameraTarget.unit || null}
          units={report.units || []}
          autoCamera={cameraTarget.autoCamera === true}
          onClose={() => setCameraTarget(null)}
          onPhotoSaved={(updatedReport) => {
            if (updatedReport) setReport(updatedReport);
          }}
        />
      )}

      {confirmRegen && (
        <ConfirmDialog
          title="Luodaanko yhteenveto uudelleen?"
          description="Nykyinen yhteenveto ja kaikki siihen tekemäsi muokkaukset korvataan uudella AI-luonnoksella. Tätä ei voi peruuttaa."
          confirmLabel="Kyllä, luo uudelleen"
          cancelLabel="Peruuta"
          variant="warning"
          onCancel={() => setConfirmRegen(false)}
          onConfirm={async () => {
            setConfirmRegen(false);
            await runGenerateSummary();
          }}
        />
      )}

    </div>
  );
}

/**
 * ConfirmDialog — small modal for destructive confirmations
 * (e.g., regenerating summary which overwrites user edits).
 */
function ConfirmDialog({ title, description, confirmLabel = "Vahvista", cancelLabel = "Peruuta", variant = "default", onConfirm, onCancel }) {
  const confirmCls = variant === "warning"
    ? "bg-amber-500 hover:bg-amber-600 text-white"
    : "bg-emerald-500 hover:bg-emerald-600 text-white";
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="confirm-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-semibold text-slate-900 text-base" data-testid="confirm-dialog-title">{title}</h2>
            {description && (
              <p className="text-sm text-slate-600 mt-1 leading-relaxed" data-testid="confirm-dialog-description">{description}</p>
            )}
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn-ghost text-sm"
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${confirmCls}`}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * TopSummaryCard — prominent, inline-editable AI summary at the top of the dashboard.
 * Always-on toggle between rendered preview and editable textarea.
 * Office workers usually only check the summary + deficiencies, so this is the
 * most important block on the page.
 */
function TopSummaryCard({ report, generating, onGenerate, onSave }) {
  // Yhteenvedon tekstikenttä on oletuksena auki — käyttäjä näkee sisällön
  // ja voi muokata sitä ilman erillistä "Muokkaa"-klikkiä.
  const [editing, setEditing] = useState(true);
  const hasSummary = !!(report.ai_summary && report.ai_summary.trim());

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 mb-4"
      data-testid="detail-summary-card"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
          Raportin yhteenveto
        </div>
        <div className="flex items-center gap-4">
          {hasSummary && (
            <button
              onClick={() => setEditing((v) => !v)}
              className="text-xs font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="summary-toggle-edit-top"
            >
              <Pencil className="h-3 w-3" />
              {editing ? "Esikatsele" : "Muokkaa"}
            </button>
          )}
          <button
            onClick={onGenerate}
            disabled={generating}
            className="text-xs font-medium text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="summary-generate-button-top"
            title="AI-avustaja ehdottaa yhteenvedon aineiston pohjalta"
          >
            <Sparkles className="h-3 w-3" />
            {generating ? "Luodaan…" : "Ehdota tekstiä"}
          </button>
        </div>
      </div>

      {!hasSummary && !editing && (
        <div className="text-sm text-slate-500 py-1" data-testid="summary-empty-top">
          Ei vielä kirjoitettu.{" "}
          <button
            onClick={() => setEditing(true)}
            className="text-slate-800 font-semibold hover:underline"
            data-testid="summary-start-writing"
          >
            Kirjoita yhteenveto →
          </button>
        </div>
      )}

      {hasSummary && !editing && (
        <div className="text-[15px] text-slate-800 leading-relaxed report-prose" data-testid="summary-preview-top">
          <MarkdownLight text={report.ai_summary} />
        </div>
      )}

      {editing && (
        <div className="mt-1">
          <DebouncedSummary value={report.ai_summary || ""} onSave={onSave} rows={10} />
        </div>
      )}
    </div>
  );
}


function SaveIndicator({ state }) {
  return (
    <div className="text-xs text-slate-500 inline-flex items-center gap-1.5" data-testid="save-indicator">
      {state === "saving" ? (
        <><Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" /> <span>Tallennetaan…</span></>
      ) : (
        <><Check className="h-3.5 w-3.5 text-emerald-500" /> <span>Tallennettu</span></>
      )}
    </div>
  );
}

function ReadOnlyField({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-1.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 -mt-0.5 truncate">{value}</div>
    </div>
  );
}

function InlineField({ label, value, onSave, testId, placeholder }) {
  const [v, setV] = useState(value || "");
  const timerRef = useRef(null);
  const localRef = useRef(value || "");

  useEffect(() => {
    if (timerRef.current !== null) return;
    const next = value || "";
    if (localRef.current === next) return;
    localRef.current = next;
    setV(next);
  }, [value]);

  const change = (next) => {
    setV(next);
    localRef.current = next;
    if (timerRef.current) {
      clearTimeout(timerRef.current.id);
    }
    const h = { id: null };
    timerRef.current = h;
    h.id = setTimeout(async () => {
      const toSave = localRef.current;
      try {
        await onSave(toSave);
      } finally {
        if (timerRef.current === h) {
          timerRef.current = null;
        }
      }
    }, 600);
  };

  const commit = () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current.id);
    const toSave = localRef.current;
    timerRef.current = null;
    onSave(toSave);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <input
        value={v}
        onChange={(e) => change(e.target.value)}
        onBlur={commit}
        placeholder={placeholder || "—"}
        className="w-full bg-transparent outline-none text-sm font-medium text-slate-900 -mt-0.5"
        data-testid={testId}
      />
    </div>
  );
}

function InlineTitle({ value, onSave, testId }) {
  const [v, setV] = useState(value || "");
  const timerRef = useRef(null);
  const localRef = useRef(value || "");
  useEffect(() => {
    if (timerRef.current !== null) return;
    const next = value || "";
    if (localRef.current === next) return;
    localRef.current = next;
    setV(next);
  }, [value]);
  const change = (next) => {
    setV(next);
    localRef.current = next;
    if (timerRef.current) {
      clearTimeout(timerRef.current.id);
    }
    const h = { id: null };
    timerRef.current = h;
    h.id = setTimeout(async () => {
      const toSave = localRef.current;
      try {
        await onSave(toSave);
      } finally {
        if (timerRef.current === h) {
          timerRef.current = null;
        }
      }
    }, 600);
  };
  const commit = () => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current.id);
    const toSave = localRef.current;
    timerRef.current = null;
    onSave(toSave);
  };
  return (
    <input
      value={v}
      onChange={(e) => change(e.target.value)}
      onBlur={commit}
      placeholder="Raportin otsikko"
      className="mt-1 w-full bg-transparent outline-none font-display text-2xl md:text-3xl font-semibold text-slate-900 focus:bg-slate-50 focus:px-2 rounded-lg transition-colors"
      data-testid={testId}
    />
  );
}

function InlineDateField({ label, value, onSave, testId }) {
  // Expect ISO datetime or date; convert to yyyy-mm-dd for <input type="date" />
  const toDateInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    } catch { return ""; }
  };
  const [v, setV] = useState(toDateInput(value));
  const skipRef = useRef(false);
  useEffect(() => { if (!skipRef.current) setV(toDateInput(value)); }, [value]);
  const change = (next) => {
    setV(next);
    skipRef.current = true;
    // Save immediately on date pick
    Promise.resolve(onSave(next || null)).finally(() => { skipRef.current = false; });
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <input
        type="date"
        value={v}
        onChange={(e) => change(e.target.value)}
        className="w-full bg-transparent outline-none text-sm font-medium text-slate-900 -mt-0.5"
        data-testid={testId}
      />
    </div>
  );
}

function MobileMoreMenu({ onSummary, onPdf, onShare, onMove, busy }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const item = (label, Icon, onClick, key, extraClass = "") => (
    <button
      key={key}
      type="button"
      onClick={() => { onClick(); setOpen(false); }}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${extraClass}`}
      data-testid={`mobile-more-${key}`}
    >
      <Icon className="h-4 w-4 text-slate-500" />
      <span>{label}</span>
    </button>
  );

  return (
    <div ref={ref} className="relative md:hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-ghost text-sm"
        data-testid="mobile-more-button"
        aria-expanded={open}
      >
        <MoreVertical className="h-4 w-4" /> Lisää
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid="mobile-more-menu">
          {item(busy ? "Luodaan…" : "Luo yhteenveto", Sparkles, onSummary, "summary")}
          {item("Lataa PDF", Download, onPdf, "pdf")}
          {item("Jaa linkki", Share2, onShare, "share")}
          {item("Siirrä kohteeseen", Building2, onMove, "move")}
        </div>
      )}
    </div>
  );
}

function ContactDetailsBlock({ contact, onSave }) {
  // Local state, debounced save to avoid spamming PATCH and to prevent typing-bug.
  const [c, setC] = useState({
    name: contact?.name || "",
    phone: contact?.phone || "",
    email: contact?.email || "",
    address: contact?.address || "",
    notes: contact?.notes || "",
  });
  const dirtyRef = useRef(false);
  const lastSeen = useRef(JSON.stringify(contact || {}));
  const timer = useRef(null);

  useEffect(() => {
    const key = JSON.stringify(contact || {});
    if (key === lastSeen.current) return;
    lastSeen.current = key;
    if (dirtyRef.current) return;
    setC({
      name: contact?.name || "",
      phone: contact?.phone || "",
      email: contact?.email || "",
      address: contact?.address || "",
      notes: contact?.notes || "",
    });
  }, [contact]);

  const update = (patch) => {
    dirtyRef.current = true;
    const next = { ...c, ...patch };
    setC(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const cleaned = Object.fromEntries(
          Object.entries(next).map(([k, v]) => [k, (v || "").trim() || null])
        );
        await onSave(cleaned);
        lastSeen.current = JSON.stringify(cleaned);
      } finally {
        dirtyRef.current = false;
      }
    }, 600);
  };

  const hasAny = !!(c.name || c.phone || c.email || c.address || c.notes);

  return (
    <details
      className="group rounded-2xl bg-slate-50/60 border border-slate-200 px-4 py-3 mb-5"
      open={hasAny}
      data-testid="detail-contact-block"
    >
      <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-semibold text-slate-700 hover:text-slate-900">
        <span>
          Tarkemmat yhteystiedot{" "}
          <span className="text-slate-400 font-normal text-xs">(yksityisasiakkaille / työmaalle)</span>
        </span>
        <span className="text-xs text-slate-400 group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <InlineLite label="Yhteyshenkilö" value={c.name} onChange={(v) => update({ name: v })} placeholder="esim. Matti Meikäläinen" testId="detail-contact-name" />
        <InlineLite label="Puhelin" value={c.phone} onChange={(v) => update({ phone: v })} placeholder="040 123 4567" inputMode="tel" testId="detail-contact-phone" />
        <InlineLite label="Sähköposti" value={c.email} onChange={(v) => update({ email: v })} placeholder="nimi@yritys.fi" inputMode="email" testId="detail-contact-email" />
        <InlineLite label="Osoite" value={c.address} onChange={(v) => update({ address: v })} placeholder="Esimerkkitie 1, 00100 Helsinki" testId="detail-contact-address" />
        <div className="sm:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Lisätiedot</div>
          <textarea
            value={c.notes}
            onChange={(e) => update({ notes: e.target.value })}
            placeholder="esim. Avain piilossa kynnyksellä, koira pihalla, paras tavoittaa klo 16 jälkeen…"
            rows={2}
            className="input-base resize-y text-sm"
            data-testid="detail-contact-notes"
          />
        </div>
      </div>
    </details>
  );
}

function InlineLite({ label, value, onChange, placeholder, inputMode, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className="input-base text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function FollowUpEstimateModal({ report, followUps, onClose, onSave }) {
  const [estimates, setEstimates] = useState(() =>
    Object.fromEntries(
      followUps.map((u) => [
        u.id,
        u.follow_up_estimate || { hours: "", materials: "", people: "", urgency: "normal", notes: "" },
      ])
    )
  );
  const set = (uid, key, val) => setEstimates((m) => ({ ...m, [uid]: { ...m[uid], [key]: val } }));
  // eslint-disable-next-line no-unused-vars
  const _r = report;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose} data-testid="follow-up-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="font-display font-semibold text-slate-900">Arvioi jatkotyöt</div>
            <div className="text-xs text-slate-500 mt-0.5">Vapaaehtoinen alustava arvio työnjohdolle.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="follow-up-close">
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {followUps.map((u, idx) => (
            <div key={u.id} className="rounded-2xl border border-slate-100 bg-slate-50/40 p-4">
              <div className="flex items-start gap-2">
                <span className="badge badge-slate">#{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-slate-900">{u.label}</div>
                  <div className="text-sm text-slate-600 mt-0.5">{u.additional_work_text}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Tunnit</label>
                  <input type="number" min="0" step="0.5" value={estimates[u.id].hours} onChange={(e) => set(u.id, "hours", e.target.value)} className="input-base" placeholder="esim. 2" data-testid={`est-hours-${u.id}`} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Henkilömäärä</label>
                  <input type="number" min="1" max="20" value={estimates[u.id].people} onChange={(e) => set(u.id, "people", e.target.value)} className="input-base" placeholder="esim. 1" data-testid={`est-people-${u.id}`} />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Tarvikkeet</label>
                  <input value={estimates[u.id].materials} onChange={(e) => set(u.id, "materials", e.target.value)} className="input-base" placeholder="esim. Hana 1 kpl, putkimuhvi 2 kpl" data-testid={`est-materials-${u.id}`} />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Kiireellisyys</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "low", l: "Ei kiire" },
                      { v: "normal", l: "Normaali" },
                      { v: "high", l: "Kiireellinen" },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => set(u.id, "urgency", opt.v)}
                        className={`btn-mobile md:min-h-[40px] text-xs font-semibold ${
                          estimates[u.id].urgency === opt.v
                            ? opt.v === "high" ? "bg-red-500 text-white"
                              : opt.v === "low" ? "bg-slate-200 text-slate-700"
                              : "bg-emerald-500 text-white"
                            : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                        }`}
                        data-testid={`est-urgency-${u.id}-${opt.v}`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Lisähuomio</label>
                  <textarea rows={2} value={estimates[u.id].notes} onChange={(e) => set(u.id, "notes", e.target.value)} className="input-base" data-testid={`est-notes-${u.id}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="follow-up-later">Myöhemmin</button>
          <button onClick={() => onSave(estimates)} className="btn-primary text-sm" data-testid="follow-up-save">Tallenna arviot</button>
        </div>
      </div>
    </div>
  );
}

function MarkdownLight({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div>
      {lines.map((line, i) => {
        if (line.startsWith("### ")) return <h3 key={i}>{line.replace("### ", "")}</h3>;
        if (line.startsWith("## ")) return <h3 key={i}>{line.replace("## ", "")}</h3>;
        if (line.startsWith("- ")) return <li key={i} style={{ marginLeft: "1.25rem" }}>{line.replace("- ", "")}</li>;
        if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
        return <p key={i}>{line}</p>;
      })}
    </div>
  );
}

function DebouncedSummary({ value, onSave, rows = 14 }) {
  const [local, setLocal] = useState(value || "");
  const [savingState, setSavingState] = useState("idle");
  const [showPreview, setShowPreview] = useState(false);
  const timerRef = useRef(null);
  const skipRef = useRef(false);
  // Ref always pointing to latest local — used inside async callbacks
  // so we can compare what user has NOW vs what we just saved.
  const localRef = useRef(local);
  useEffect(() => { localRef.current = local; }, [local]);

  useEffect(() => {
    // Don't ever overwrite user's in-flight edits with parent's saved value.
    if (skipRef.current) return;
    if (timerRef.current) return;
    // No-op if parent value already matches what user has locally.
    if ((value || "") === localRef.current) return;
    setLocal(value || "");
  }, [value]);

  const change = (next) => {
    setLocal(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    skipRef.current = true;
    setSavingState("saving");
    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      try {
        await onSave(next);
        // Only release the skip flag if the user hasn't typed anything new
        // while the save was in flight. Otherwise we'd risk a parent re-render
        // overwriting their fresher local content (cursor would jump to end).
        if (localRef.current === next) {
          skipRef.current = false;
        }
        setSavingState("saved");
        setTimeout(() => setSavingState("idle"), 800);
      } catch {
        // On failure, release the skip so the next sync can recover.
        skipRef.current = false;
        setSavingState("idle");
      }
    }, 600);
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-end gap-3 text-xs">
        <button
          onClick={() => setShowPreview((v) => !v)}
          className="text-slate-500 hover:text-emerald-700 font-semibold"
          data-testid="summary-toggle-preview"
        >
          {showPreview ? "Muokkaa" : "Esikatsele"}
        </button>
        <span className="text-[10px] uppercase tracking-wider font-semibold">
          {savingState === "saving" && <span className="text-amber-500">Tallennetaan…</span>}
          {savingState === "saved" && <span className="text-emerald-600">Tallennettu</span>}
        </span>
      </div>
      {showPreview ? (
        <div className="rounded-xl border border-slate-100 bg-slate-50 p-5 report-prose" data-testid="summary-preview">
          <MarkdownLight text={local} />
        </div>
      ) : (
        <textarea
          value={local}
          onChange={(e) => change(e.target.value)}
          rows={rows}
          placeholder="Kirjoita tähän yhteenveto asiakkaalle..."
          className="input-base font-mono text-sm leading-relaxed"
          data-testid="summary-edit-textarea"
        />
      )}
    </div>
  );
}

function ShareLinksDialog({ shareToken, editToken, onClose, onCopy }) {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  const readUrl = shareToken ? `${base}/r/${shareToken}` : "";
  const editUrl = editToken ? `${base}/r-edit/${editToken}` : "";
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" data-testid="share-dialog">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[85vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">Jaa raportti</div>
            <h2 className="font-display text-lg font-semibold text-slate-900 mt-0.5">Kaksi eri linkkiä</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg" data-testid="share-dialog-close">
            <span className="text-2xl leading-none">×</span>
          </button>
        </div>

        <div className="p-5 space-y-5">
          <LinkRow
            label="Muokkauslinkki — asentajalle"
            help="Asentaja voi täyttää yksiköitä, lisätä kuvia, havaintoja ja merkitä valmiiksi. Ei vaadi kirjautumista. Ei pääse muihin raportteihin."
            url={editUrl}
            loading={!editToken}
            color="emerald"
            onCopy={() => onCopy(editUrl, "edit")}
            testIdPrefix="share-edit"
          />

          <LinkRow
            label="Lukulinkki — asiakkaalle"
            help="Vain luku. Asiakas näkee valmiin raportin ja voi ladata PDF:n. Ei voi muokata."
            url={readUrl}
            loading={!shareToken}
            color="slate"
            onCopy={() => onCopy(readUrl, "read")}
            testIdPrefix="share-read"
          />
        </div>
      </div>
    </div>
  );
}

function LinkRow({ label, help, url, loading, color, onCopy, testIdPrefix }) {
  const colorBg = color === "emerald" ? "bg-emerald-50/40 border-emerald-200" : "bg-slate-50 border-slate-200";
  const colorBtn = color === "emerald" ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-slate-700 hover:bg-slate-900 text-white";
  return (
    <div className={`rounded-xl border ${colorBg} p-4`}>
      <div className="font-display font-semibold text-slate-900 text-sm">{label}</div>
      <p className="text-xs text-slate-600 leading-relaxed mt-1 mb-3">{help}</p>
      <div className="flex gap-2">
        <input
          readOnly
          value={loading ? "Ladataan…" : url}
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-mono text-slate-700 outline-none"
          data-testid={`${testIdPrefix}-input`}
          onFocus={(e) => e.target.select()}
        />
        <button
          type="button"
          onClick={onCopy}
          disabled={loading}
          className={`px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 ${colorBtn}`}
          data-testid={`${testIdPrefix}-copy`}
        >
          Kopioi
        </button>
      </div>
    </div>
  );
}



/* -----------------------------------------------------------------------------
 * Layout helpers (uusi toolbar + section-group rakenne)
 * ---------------------------------------------------------------------------*/

function ActionBtn({ onClick, icon: Icon, label, testId, accent }) {
  const isAccent = accent === "emerald";
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        isAccent
          ? "flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-emerald-600 bg-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 transition-colors text-sm font-semibold text-white shadow-sm shadow-emerald-600/20"
          : "flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors text-sm font-semibold text-slate-800"
      }
    >
      <Icon className={`h-4 w-4 ${isAccent ? "text-white" : "text-slate-500"}`} />
      <span>{label}</span>
    </button>
  );
}

function MoreActionsMenu({ onMove, onReopen }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="report-more-actions"
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors text-sm font-semibold text-slate-800"
      >
        <MoreVertical className="h-4 w-4 text-slate-500" />
        <span>Lisää toimintoja</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-64 rounded-xl border border-slate-200 bg-white shadow-lg py-1.5 z-30" data-testid="more-actions-menu">
          <button
            onClick={() => { setOpen(false); onMove(); }}
            data-testid="more-actions-move"
            className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2"
          >
            <Building2 className="h-4 w-4 text-slate-400" />
            Siirrä kohteeseen
          </button>
          {onReopen && (
            <button
              onClick={() => { setOpen(false); onReopen(); }}
              data-testid="more-actions-reopen"
              className="w-full text-left px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 inline-flex items-center gap-2"
            >
              <Lock className="h-4 w-4 text-amber-500" />
              Avaa muokkaus
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionGroup({ title, description, testId, children }) {
  return (
    <section className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 md:p-5" data-testid={testId}>
      <div className="mb-4">
        <h2 className="font-display text-base md:text-lg font-semibold text-slate-900">{title}</h2>
        {description && <p className="text-xs md:text-sm text-slate-500 mt-1">{description}</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {children}
      </div>
    </section>
  );
}

function GroupCard({ icon: Icon, title, description, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-left rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors p-4 flex items-start gap-3"
    >
      <div className="h-10 w-10 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-slate-900 text-sm md:text-base">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
      </div>
      <div className="text-slate-300 flex-shrink-0 self-center">
        <ChevronRight className="h-5 w-5" />
      </div>
    </button>
  );
}


/* -----------------------------------------------------------------------------
 * PreparationSection — heti raportin luonnin jälkeen näkyvät inline-blokit
 * Työvaiheet · Havaintolistat · Vakiotarvikkeet + Kuvien aikaleima + Allekirjoitus
 * ---------------------------------------------------------------------------*/

function PreparationSection({ report, onOpenChecklist, onOpenMaterials, onOpenCustomFields, onToggle }) {
  // Työvaihe- ja havaintomäärät lasketaan yksiköiden checklististä (uniikkeja labeleita)
  const { workCount, findingCount } = (() => {
    const workSet = new Set();
    const findSet = new Set();
    for (const u of report.units || []) {
      for (const c of u.checklist || []) {
        const key = (c.label || "").trim();
        if (!key) continue;
        if (c.is_finding) findSet.add(key); else workSet.add(key);
      }
    }
    return { workCount: workSet.size, findingCount: findSet.size };
  })();
  const materialsCount = (report.materials || []).length;
  const customFieldsCount = (report.custom_fields || []).length;

  return (
    <section className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 md:p-7" data-testid="section-preparation">
      <div className="mb-6">
        <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900">Mitä asentaja täyttää työmaalla?</h2>
        <p className="text-sm md:text-base text-slate-500 mt-1.5">Näillä rakennat sisällön, jota asentaja käyttää puhelimessa.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <PrepCard
          label="Työvaiheet"
          hint="Työvaiheet, jotka asentaja kuittaa työn aikana (esim. Suodattimet vaihdettu, Kanavat puhdistettu)."
          emptyText="Ei työvaiheita lisätty"
          filledText={(n) => `${n} ${n === 1 ? "työvaihe" : "työvaihetta"} lisätty`}
          addLabel="Lisää työvaiheet"
          count={workCount}
          onClick={() => onOpenChecklist("work")}
          testId="prep-work-checklists"
        />
        <PrepCard
          label="Havaintolistat"
          hint="Havaintokohdat, joihin asentaja kirjaa poikkeamat ja huomiot (esim. Vesivuoto havaittu, Suositellaan vaihtoa)."
          emptyText="Ei havaintolistoja"
          filledText={(n) => `${n} ${n === 1 ? "havainto" : "havaintoa"}`}
          addLabel="Lisää havaintolistat"
          count={findingCount}
          onClick={() => onOpenChecklist("findings")}
          testId="prep-finding-checklists"
        />
        <PrepCard
          label="Vakiotarvikkeet"
          hint="Yleisimmin käytettävät tarvikkeet. Asentaja lisää ne yhdellä painalluksella työmaalla."
          emptyText="Ei vakiotarvikkeita"
          filledText={(n) => `${n} ${n === 1 ? "vakiotarvike" : "vakiotarviketta"}`}
          addLabel="Lisää vakiotarvikkeet"
          count={materialsCount}
          onClick={onOpenMaterials}
          testId="prep-materials"
        />
        <PrepCard
          label="Muut kerättävät tiedot"
          hint="Yrityksesi omia tietoja, jotka asentaja täyttää tarvittaessa. Esim. Kesä-/talvipellin asento, Huoltoluokka, Käyttötunnit."
          emptyText="Ei muita tietoja"
          filledText={(n) => `${n} ${n === 1 ? "kenttä" : "kenttää"} lisätty`}
          addLabel="Lisää tiedot"
          count={customFieldsCount}
          onClick={onOpenCustomFields}
          testId="prep-custom-fields"
          optional
        />
      </div>

      {/* Raportin asetukset — kaikki näkyvissä täppinä (ei erillistä nappia) */}
      <div className="mt-4 pt-4 border-t border-slate-100 space-y-0.5">
        <MiniToggle
          label="Kuvien aikaleima"
          checked={!!report.image_timestamps}
          onChange={(v) => onToggle({ image_timestamps: v })}
          testId="prep-toggle-timestamps"
        />
        <MiniToggle
          label="Pyydä allekirjoitus"
          checked={!!report.request_signature}
          onChange={(v) => onToggle({ request_signature: v })}
          testId="prep-toggle-signature"
        />
        <MiniToggle
          label="Piilota työvaiheet raportilta"
          checked={!!report.hide_work_checklists}
          onChange={(v) => onToggle({ hide_work_checklists: v })}
          testId="prep-toggle-hide-work"
        />
        <MiniToggle
          label="Piilota havaintolista raportilta"
          checked={!!report.hide_finding_checklists}
          onChange={(v) => onToggle({ hide_finding_checklists: v })}
          testId="prep-toggle-hide-finding"
        />
        <MiniToggle
          label="Piilota tarvikkeet raportilta"
          checked={!!report.hide_materials}
          onChange={(v) => onToggle({ hide_materials: v })}
          testId="prep-toggle-hide-materials"
        />
        <MiniToggle
          label="Piilota havainnot raportilta"
          checked={!!report.hide_findings_section}
          onChange={(v) => onToggle({ hide_findings_section: v })}
          testId="prep-toggle-hide-findings-section"
        />
      </div>
    </section>
  );
}

function PrepCard({ label, hint, emptyText, filledText, addLabel, count, onClick, testId, optional }) {
  const hasContent = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-2xl border p-5 md:p-6 transition-all h-full flex flex-col ${
        hasContent
          ? "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"
          : "bg-emerald-50/40 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
      }`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-display font-semibold text-slate-900 text-base md:text-lg leading-snug">
          {label}
          {optional && <span className="ml-1.5 text-xs font-normal text-slate-400">(valinnainen)</span>}
        </div>
        <ChevronRight className={`h-4 w-4 flex-shrink-0 mt-1 ${hasContent ? "text-slate-400" : "text-emerald-600"}`} />
      </div>
      <div className="text-xs md:text-sm text-slate-500 mt-1.5 leading-relaxed line-clamp-2">{hint}</div>
      <div className="mt-auto pt-4">
        <div className={`text-xs md:text-sm inline-flex items-center gap-1.5 ${hasContent ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>
          <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[10px] font-bold ${hasContent ? "bg-emerald-100 text-emerald-700" : "border border-slate-300 text-transparent"}`}>
            {hasContent ? "✓" : "○"}
          </span>
          <span>{hasContent ? filledText(count) : emptyText}</span>
        </div>
        <div
          className={`mt-3 inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold ${
            hasContent ? "text-slate-700" : "text-emerald-800"
          }`}
          data-testid={`${testId}-btn`}
        >
          {hasContent ? "Muokkaa" : addLabel}
          <ChevronRight className={`h-3.5 w-3.5 ${hasContent ? "text-slate-400" : "text-emerald-600"}`} />
        </div>
      </div>
    </button>
  );
}

function MiniToggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2.5 py-1.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 accent-emerald-600"
        data-testid={testId}
      />
      <span className="text-sm text-slate-700 group-hover:text-slate-900">{label}</span>
    </label>
  );
}

